import { SpinButton, Toggle } from "@fluentui/react";
import { observer } from "mobx-react";
import { useRef, type SyntheticEvent } from "react";
import { store } from "../../store";
import { Section } from "./section";

// The timer length suggested when the logout timer is switched on.
const defaultMinutesUntilLogout = 50;
// Bounds of `minutesUntilLogout`, mirroring the backend validation.
const minMinutesUntilLogout = 1;
const maxMinutesUntilLogout = 1440;

export interface ChecklistLogoutTimerProps {
    /** Minutes configured on the checklist, or `null`/`undefined` when off. */
    readonly minutesUntilLogout: number | null | undefined;
    /** Called with the new value, `null` meaning "no logout timer". */
    readonly onChange: (minutesUntilLogout: number | null) => void;
    /** Prefix of the translation keys, e.g. `formUpdatePreOpsChecklist`. */
    readonly translationPrefixKey: string;
}

/**
 * The logout timer section of a pre-ops checklist form: a switch in the section
 * header, and the length of the timer in its body.
 *
 * An unset timer means "no logout", so the switch is derived from whether a
 * value is present rather than being stored separately. That also keeps the
 * recheck slider's `0` from having to mean two different things.
 *
 * The description stays visible whether the timer is on or off — it is what
 * tells the reader what the switch would do.
 */
export const ChecklistLogoutTimer = observer(
    ({
        minutesUntilLogout,
        onChange,
        translationPrefixKey,
    }: ChecklistLogoutTimerProps): JSX.Element => {
        // Kept while the timer is switched off, so that switching it back on
        // restores what the user had entered instead of resetting to the
        // default. Nothing renders from it, so it stays out of state.
        const minutesDraft = useRef(defaultMinutesUntilLogout);

        // Reading a checklist maps a `null` timer to `undefined`, so both have
        // to count as "off" here.
        const enabled = minutesUntilLogout != null;

        const translate = (key: string): string =>
            store.i18n.t(`${translationPrefixKey}.minutesUntilLogout.${key}`);

        const setEnabled = (_event: unknown, isEnabled: boolean | undefined): void => {
            if (isEnabled === true) {
                onChange(minutesDraft.current);
                return;
            }
            minutesDraft.current = minutesUntilLogout ?? defaultMinutesUntilLogout;
            onChange(null);
        };

        const setMinutes = (
            _event: SyntheticEvent<HTMLElement>,
            newMinutes?: string | undefined,
        ): void => {
            const minutes = Number.parseInt(newMinutes ?? "", 10);
            if (Number.isNaN(minutes)) {
                return;
            }
            minutesDraft.current = minutes;
            onChange(minutes);
        };

        return (
            <Section
                title={translate("title")}
                withPadding
                actionButtons={
                    <Toggle
                        ariaLabel={translate("title")}
                        checked={enabled}
                        onChange={setEnabled}
                        styles={{ root: { marginBottom: 0 } }}
                    />
                }
            >
                <div>
                    <p className="text-muted-foreground m-0 max-w-[44em] text-xs leading-normal">
                        {translate("description")}
                    </p>
                    {enabled && (
                        <div className="mt-2.5 flex items-center gap-2.5">
                            <SpinButton
                                label={translate("label")}
                                value={(minutesUntilLogout ?? defaultMinutesUntilLogout).toString()}
                                onChange={setMinutes}
                                min={minMinutesUntilLogout}
                                max={maxMinutesUntilLogout}
                                styles={{
                                    root: { width: "auto" },
                                    spinButtonWrapper: { width: "72px" },
                                }}
                            />
                            <span className="text-muted-foreground text-sm">
                                {translate("suffix")}
                            </span>
                        </div>
                    )}
                </div>
            </Section>
        );
    },
);
